// src/components/auth/UserMenu.js
import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { USER_TIERS } from '../../services/UserService';
import PremiumUpgradeModal from '../PremiumUpgradeModal';

function UserMenu({ onShowAuthModal, onShowAccountSettings }) {
  const { currentUser, logout, userTier, updateUserTier, isAuthenticated } = useAuth();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const menuRef = useRef(null);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isUpgradeModalOpen, setIsUpgradeModalOpen] = useState(false);

  // Close the menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsMenuOpen(false);
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen]);

  const handleLogout = async () => {
    try {
      setIsLoggingOut(true);
      await logout();
      setIsMenuOpen(false);
      setIsLoggingOut(false);
    } catch (error) {
      console.error('Logout failed:', error);
      setIsLoggingOut(false);
    }
  };

  const handleLogin = () => {
    if (onShowAuthModal) {
      onShowAuthModal('login');
    }
  };

  const handleSignup = () => {
    if (onShowAuthModal) {
      onShowAuthModal('signup');
    }
  };

  const handleAccountSettings = () => {
    setIsMenuOpen(false);
    if (onShowAccountSettings) {
      onShowAccountSettings();
    }
  };

  const handleOpenUpgradeModal = () => {
    setIsMenuOpen(false);
    setIsUpgradeModalOpen(true);
  };

  const handleSubscriptionSuccess = async (subscriptionData) => {
    try {
      // Update user tier to pro
      if (currentUser) {
        await updateUserTier(USER_TIERS.PRO);
      }
      setIsUpgradeModalOpen(false);
    } catch (error) {
      console.error('Error updating user tier:', error);
    }
  };

  const getTierBadge = () => {
    // Make sure we're always showing at least BASIC tier for authenticated users
    const effectiveTier = isAuthenticated && (!userTier || userTier === USER_TIERS.NONE) 
      ? USER_TIERS.BASIC 
      : userTier;
      
    switch (effectiveTier) {
      case USER_TIERS.PRO:
        return (
          <span className="ml-2 bg-gradient-to-r from-indigo-600 to-purple-600 text-white text-xs px-2 py-0.5 rounded-full">
            Premium
          </span>
        );
      case USER_TIERS.BASIC:
        return (
          <span className="ml-2 bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded-full">
            Basic
          </span>
        );
      default:
        return (
          <span className="ml-2 bg-gray-100 text-gray-800 text-xs px-2 py-0.5 rounded-full">
            Guest
          </span>
        );
    }
  };

  // Get user's initials for avatar
  const getUserInitials = () => {
    if (currentUser?.name) {
      const nameParts = currentUser.name.split(' ');
      if (nameParts.length >= 2) {
        return `${nameParts[0][0]}${nameParts[1][0]}`.toUpperCase();
      }
      return currentUser.name[0].toUpperCase();
    }
    
    if (currentUser?.email) {
      return currentUser.email[0].toUpperCase();
    }
    
    return 'U';
  };

  // User is not authenticated
  if (!isAuthenticated) {
    return (
      <div className="flex items-center space-x-2">
        <button
          onClick={handleLogin}
          className="px-3 py-1.5 text-sm bg-white hover:bg-gray-50 text-blue-600 font-medium rounded-md border border-blue-200 shadow-sm transition-colors hidden sm:block"
        >
          Log In
        </button>
        <button
          onClick={handleSignup}
          className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-md shadow-sm transition-colors flex items-center"
        >
          <span className="mr-1 hidden sm:inline">✨</span>
          <span className="sm:hidden">Log In</span>
          <span className="hidden sm:inline">Sign Up</span>
        </button>
      </div>
    );
  }

  // User is authenticated
  return (
    <div className="relative" ref={menuRef}>
      <button
        onClick={() => setIsMenuOpen(!isMenuOpen)}
        className="flex items-center space-x-2 hover:opacity-80 transition-opacity focus:outline-none"
        aria-expanded={isMenuOpen}
        aria-haspopup="true"
      >
        <div className="h-9 w-9 rounded-full bg-gradient-to-r from-blue-600 to-blue-500 text-white flex items-center justify-center font-medium shadow-sm">
          {getUserInitials()}
        </div>
        <span className="font-medium text-gray-800 hidden md:block">
          {currentUser?.name || currentUser?.email?.split('@')[0]}
          {getTierBadge()}
        </span>
        <svg className="w-4 h-4 text-gray-500 hidden md:block" viewBox="0 0 20 20" fill="currentColor">
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 011.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </button>

      {isMenuOpen && (
        <div className="absolute right-0 mt-2 w-64 bg-white rounded-lg shadow-lg z-10 overflow-hidden border border-gray-200 transform origin-top-right transition-all duration-200">
          <div className="p-4 border-b border-gray-100 bg-gray-50">
            <p className="font-medium text-gray-900 truncate">
              {currentUser?.name || ''}
            </p>
            <p className="text-xs text-gray-500 truncate mt-0.5">
              {currentUser?.email}
            </p>
            <div className="mt-2">
              {userTier === USER_TIERS.PRO ? (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gradient-to-r from-indigo-600 to-purple-600 text-white">
                  <span className="mr-1">✨</span>
                  Premium Tier
                </span>
              ) : (
                <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  Basic Tier
                </span>
              )}
            </div>
          </div>
          
          <div className="py-1">
            <button
              onClick={handleAccountSettings}
              className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
              role="menuitem"
            >
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
                Account Settings
              </div>
            </button>
            
            {userTier !== USER_TIERS.PRO && (
              <button
                onClick={handleOpenUpgradeModal}
                className="block w-full text-left px-4 py-2 text-sm text-indigo-600 hover:bg-indigo-50 transition-colors"
                role="menuitem"
              >
                <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                  </svg>
                  Upgrade to Pro
                </div>
              </button>
            )}
            
            {userTier === USER_TIERS.PRO && (
              <button
                onClick={handleAccountSettings}
                className="block w-full text-left px-4 py-2 text-sm text-indigo-600 hover:bg-indigo-50 transition-colors"
                role="menuitem"
              >
                <div className="flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  Manage Subscription
                </div>
              </button>
            )}
            
            <button
              onClick={handleLogout}
              disabled={isLoggingOut}
              className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
              role="menuitem"
            >
              <div className="flex items-center">
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-3 text-red-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
                {isLoggingOut ? 'Logging out...' : 'Log Out'}
              </div>
            </button>
          </div>
        </div>
      )}
      
      {/* Premium Upgrade Modal */}
      <PremiumUpgradeModal
        isOpen={isUpgradeModalOpen}
        onClose={() => setIsUpgradeModalOpen(false)}
        onSuccess={handleSubscriptionSuccess}
      />
    </div>
  );
}

export default UserMenu;